import {useEffect, useState} from 'react';
import {getDisclosureCountsByParentIds, subscribeToUserFintel} from '../../../firebase/firestore/reports';
import {getIcrMapByNumbers, resolveReportLinkedIcrNumber} from '../../../firebase/firestore/icr';
import {enrichDraftsWithIcr} from './useUserDrafts';

function resolveIcrNumber(report) {
    return resolveReportLinkedIcrNumber(report);
}

/**
 * Custom hook to subscribe to user FINTEL reports
 * FINTEL are reports where status === 'approved' && productType.value !== 'disclosure'
 * @param {string} userId - The user ID to fetch reports for
 * @returns {Object} - Object containing reports, loading state, and error
 */
export const useUserFintel = (userId) => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [error, setError] = useState(null);

    const removeReport = (reportId) =>
        setReports(prev => prev.filter(r => r.id !== reportId));

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const unsubscribe = subscribeToUserFintel(
            userId,
            async (data) => {
                try {
                    const fintelReports = Array.isArray(data) ? data : [];
                    const [counts, icrByNumber] = await Promise.all([
                        getDisclosureCountsByParentIds(fintelReports.map((r) => r.id)),
                        (() => {
                            const icrNumbers = fintelReports.map(resolveIcrNumber).filter(Boolean);
                            return icrNumbers.length ? getIcrMapByNumbers(icrNumbers) : Promise.resolve({});
                        })(),
                    ]);
                    const enriched = enrichDraftsWithIcr(fintelReports, icrByNumber);
                    setReports(enriched.map((r) => ({
                        ...r,
                        disclosureCount: counts[r.id] ?? 0,
                    })));
                    setError(null);
                } catch (err) {
                    console.error('Failed to load FINTEL data', err);
                    setReports(Array.isArray(data) ? data : []);
                    setError(err);
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                console.error('Failed to subscribe to FINTEL reports', err);
                setError(err);
                setLoading(false);
            }
        );

        return () => {
            try {
                unsubscribe && unsubscribe();
            } catch (e) {
                // Cleanup error - safe to ignore
            }
        };
    }, [userId]);

    return {reports, loading, error, removeReport};
};
